<template>
    <div>
        <Head title="Bâtiments" />

        <!-- Toolbar -->
        <div class="flex flex-col md:flex-row gap-3 mb-4 justify-between items-center">
            <search-input v-model="form.search" placeholder="Rechercher un bâtiment..." class="w-full max-w-md" @reset="reset" />
            <div class="flex gap-2 items-center">
                <select-input v-model="form.status" class="w-40">
                    <option :value="null">Tous les statuts</option>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="en_travaux">En travaux</option>
                </select-input>
                <button class="btn-indigo" @click="openCreate">+ Nouveau bâtiment</button>
            </div>
        </div>

        <!-- Table -->
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <table class="min-w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                    <th class="px-6 py-4">Nom</th>
                    <th class="px-6 py-4">Code</th>
                    <th class="px-6 py-4">Type</th>
                    <th class="px-6 py-4">Statut</th>
                    <th class="px-6 py-4">Surface m²</th>
                    <th class="px-6 py-4">Équipements</th>
                    <th class="px-6 py-4">Actions</th>
                </tr>
                <tr v-for="b in buildings.data" :key="b.id" class="hover:bg-gray-100 border-t">
                    <td class="px-6 py-3">
                        <Link :href="route('buildings.show', b.id)" class="font-medium text-indigo-600 hover:underline">{{ b.name }}</Link>
                    </td>
                    <td class="px-6 py-3 text-gray-500">{{ b.code || '—' }}</td>
                    <td class="px-6 py-3">{{ b.type || '—' }}</td>
                    <td class="px-6 py-3">
                        <span :class="statusClass(b.status)" class="px-2 py-0.5 rounded-full text-xs font-semibold">{{ b.status || '—' }}</span>
                    </td>
                    <td class="px-6 py-3">{{ b.surface_m2 ? b.surface_m2 + ' m²' : '—' }}</td>
                    <td class="px-6 py-3">{{ b.equipment_count }}</td>
                    <td class="px-6 py-3 flex gap-2">
                        <button class="text-indigo-600 hover:underline text-sm" @click="openEdit(b)">Modifier</button>
                        <button class="text-red-500 hover:underline text-sm" @click="destroy(b.id)">Supprimer</button>
                    </td>
                </tr>
                <tr v-if="buildings.data.length === 0">
                    <td class="px-6 py-4 border-t" colspan="7">Aucun bâtiment trouvé.</td>
                </tr>
            </table>
        </div>
        <pagination class="mt-4" :links="buildings.links" />

        <!-- Modal Créer / Modifier -->
        <div v-if="modal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                <h2 class="text-lg font-bold mb-4">{{ editing ? 'Modifier le bâtiment' : 'Nouveau bâtiment' }}</h2>
                <form @submit.prevent="submit">
                    <div class="grid grid-cols-2 gap-3">
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Nom *</label>
                            <input v-model="form2.name" type="text" class="form-input w-full" required maxlength="150" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Code</label>
                            <input v-model="form2.code" type="text" class="form-input w-full" maxlength="20" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Type</label>
                            <input v-model="form2.type" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Surface (m²)</label>
                            <input v-model="form2.surface_m2" type="number" step="0.01" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Année de construction</label>
                            <input v-model="form2.year_built" type="number" min="1900" max="2100" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Étages</label>
                            <input v-model="form2.floors" type="number" min="0" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Statut</label>
                            <select v-model="form2.status" class="form-input w-full">
                                <option value="actif">Actif</option>
                                <option value="inactif">Inactif</option>
                                <option value="en_travaux">En travaux</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Organisation</label>
                            <select v-model="form2.organization_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="o in organizations" :key="o.id" :value="o.id">{{ o.name }}</option>
                            </select>
                        </div>
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Description</label>
                            <textarea v-model="form2.description" class="form-input w-full" rows="3"></textarea>
                        </div>
                    </div>
                    <div class="flex justify-end gap-2 mt-4">
                        <button type="button" class="btn-white" @click="modal = false">Annuler</button>
                        <button type="submit" class="btn-indigo" :disabled="submitting">
                            {{ submitting ? 'Enregistrement...' : 'Enregistrer' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import SelectInput from '@/Shared/SelectInput'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import throttle from 'lodash/throttle'

export default {
    layout: Layout,
    components: { Link, Head, Pagination, SearchInput, SelectInput },
    props: {
        buildings: Object,
        filters: Object,
        organizations: Array,
    },
    remember: 'form',
    data() {
        return {
            form: {
                search: this.filters.search,
                status: this.filters.status ?? null,
            },
            modal: false,
            editing: null,
            submitting: false,
            form2: this.emptyForm(),
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('buildings.index'), pickBy(this.form), { replace: true, preserveState: true })
            }, 150),
        },
    },
    methods: {
        emptyForm() {
            return { name: '', code: '', type: '', organization_id: null, surface_m2: '', year_built: '', floors: '', status: 'actif', description: '' }
        },
        statusClass(s) {
            return {
                'bg-green-100 text-green-700': s === 'actif',
                'bg-red-100 text-red-700': s === 'inactif',
                'bg-yellow-100 text-yellow-700': s === 'en_travaux',
                'bg-gray-100 text-gray-600': !s,
            }
        },
        openCreate() {
            this.editing = null
            this.form2 = this.emptyForm()
            this.modal = true
        },
        openEdit(b) {
            this.editing = b.id
            this.form2 = { name: b.name, code: b.code || '', type: b.type || '', organization_id: b.organization_id || null, surface_m2: b.surface_m2 || '', year_built: b.year_built || '', floors: b.floors || '', status: b.status || 'actif', description: b.description || '' }
            this.modal = true
        },
        submit() {
            this.submitting = true
            if (this.editing) {
                this.$inertia.put(this.route('buildings.update', this.editing), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            } else {
                this.$inertia.post(this.route('buildings.store'), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            }
        },
        destroy(id) {
            if (confirm('Supprimer ce bâtiment ?')) {
                this.$inertia.delete(this.route('buildings.destroy', id))
            }
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
